import { useRef, useEffect, useCallback } from 'react'
import { hsvToRgb, rgbToHsv, hexToRgb, rgbToHex } from '../utils/colors'

const BORDER_SIZE = 1

const HexPicker = ({ onChange, value }) => {
  const satCt = useRef(null)
  const satCanvas = useRef(null)
  const satPointer = useRef(null)
  const hueCt = useRef(null)
  const hueCanvas = useRef(null)
  const huePointer = useRef(null)
  const hsv = useRef([0, 0, 0])
  const updateValueTimeout = useRef(null)

  useEffect(() => {
    paintHue()
    valueChangedFromOutside(value)
    handleMouse(satCt.current, onSatMouse)
    handleMouse(hueCt.current, onHueMouse)
  }, [onHueMouse, onSatMouse, value, valueChangedFromOutside])

  useEffect(() => {
    valueChangedFromOutside(value)
  }, [value, valueChangedFromOutside])

  const paintHue = () => {
    const w = hueCt.current.offsetWidth - 2 * BORDER_SIZE
    const h = hueCt.current.offsetHeight - 2 * BORDER_SIZE
    hueCanvas.current.width = w
    hueCanvas.current.style.width = w + 'px'
    hueCanvas.current.height = h
    hueCanvas.current.style.height = h + 'px'
    const ctx = hueCanvas.current.getContext('2d')
    const imdata = ctx.createImageData(w, h)
    const data = imdata.data
    for (let p = 0, len = data.length; p < len; p += 4) {
      const pix = p / 4
      const y = Math.floor(pix / w)
      const hue = y / h
      const [r, g, b] = hsvToRgb(hue, 1, 1)
      data[p] = r
      data[p + 1] = g
      data[p + 2] = b
      data[p + 3] = 255
    }
    ctx.putImageData(imdata, 0, 0)
  }

  const paintSat = () => {
    const w = satCt.current.offsetWidth - 2 * BORDER_SIZE
    const h = satCt.current.offsetHeight - 2 * BORDER_SIZE
    satCanvas.current.width = w
    satCanvas.current.style.width = w + 'px'
    satCanvas.current.height = h
    satCanvas.current.style.height = h + 'px'
    const ctx = satCanvas.current.getContext('2d')
    const imdata = ctx.createImageData(w, h)
    const data = imdata.data
    for (let p = 0, len = data.length; p < len; p += 4) {
      const pix = p / 4
      const x = pix % w
      const y = Math.floor(pix / w)
      const sat = x / w
      const val = 1 - y / h
      const [r, g, b] = hsvToRgb(hsv.current[0], sat, val)
      data[p] = r
      data[p + 1] = g
      data[p + 2] = b
      data[p + 3] = 255
    }
    ctx.putImageData(imdata, 0, 0)
  }

  const valueChangedFromOutside = useCallback((value) => {
    const rgb = hexToRgb(value)
    const currentRgb = hsvToRgb(...hsv.current)
    if (
      rgb &&
      (rgb[0] !== currentRgb[0] ||
        rgb[1] !== currentRgb[1] ||
        rgb[2] !== currentRgb[2])
    ) {
      hsv.current = rgbToHsv(...rgb)
      paintSat()
      updateSatPointer()
      updateHuePointer()
    }
  }, [])

  const hsvUpdated = useCallback(() => {
    // Throttle onChange calls
    clearTimeout(updateValueTimeout.current)
    updateValueTimeout.current = setTimeout(() => {
      const value = rgbToHex(...hsvToRgb(...hsv.current))
      onChange(value)
    }, 100)
  }, [onChange])

  const updateSatPointer = () => {
    const rect = satPointer.current.getBoundingClientRect()

    let left = satCanvas.current.offsetWidth * hsv.current[1] - rect.width / 2
    left = Math.max(
      0,
      Math.min(satCanvas.current.offsetWidth - rect.width, left)
    )

    let top =
      satCanvas.current.offsetHeight * (1 - hsv.current[2]) - rect.height / 2
    top = Math.max(
      0,
      Math.min(satCanvas.current.offsetHeight - rect.height, top)
    )

    satPointer.current.style.left = left + 'px'
    satPointer.current.style.top = top + 'px'
  }

  const updateHuePointer = () => {
    const pointerHeight = huePointer.current.getBoundingClientRect().height
    let top =
      hueCanvas.current.offsetHeight * hsv.current[0] - pointerHeight / 2
    top = Math.max(0, Math.min(hueCt.current.offsetHeight - pointerHeight, top))
    huePointer.current.style.top = top + 'px'
  }

  const onSatMouse = useCallback(
    ({ clientX, clientY }) => {
      const rect = satCanvas.current.getBoundingClientRect()

      let sat = (clientX - rect.left) / rect.width
      sat = Math.max(0, Math.min(1, sat))
      hsv.current[1] = sat

      let val = (clientY - rect.top) / rect.height
      val = 1 - Math.max(0, Math.min(1, val))
      hsv.current[2] = val

      updateSatPointer()
      hsvUpdated()
    },
    [hsvUpdated]
  )

  const onHueMouse = useCallback(
    ({ clientY }) => {
      const rect = hueCanvas.current.getBoundingClientRect()

      let hue = (clientY - rect.top) / rect.height
      hue = Math.max(0, Math.min(1, hue))
      hsv.current[0] = hue

      updateHuePointer()
      hsvUpdated()
    },
    [hsvUpdated]
  )

  const handleMouse = (el, throttleCallback) => {
    const moveListener = (e) => {
      e.preventDefault()
      throttleCallback(e)
    }

    const upListener = () => {
      document.body.removeEventListener('mousemove', moveListener)
      document.body.removeEventListener('mouseup', upListener)
    }

    const downListener = (e) => {
      document.body.addEventListener('mousemove', moveListener)
      document.body.addEventListener('mouseup', upListener)
      throttleCallback(e)
    }

    el.addEventListener('mousedown', downListener)

    return () => {
      el.removeEventListener('mousedown', downListener)
    }
  }

  return (
    <div className="velorbis-customizer-hex-picker">
      <div className="velorbis-customizer-hex-picker-sat" ref={satCt}>
        <canvas ref={satCanvas} />
        <div
          className="velorbis-customizer-hex-picker-sat-pointer"
          ref={satPointer}
        />
      </div>
      <div className="velorbis-customizer-hex-picker-hue" ref={hueCt}>
        <canvas ref={hueCanvas} />
        <div
          className="velorbis-customizer-hex-picker-hue-pointer"
          ref={huePointer}
        />
      </div>
    </div>
  )
}

export default HexPicker
